// BlockToolbar — floating toolbar attached to the top edge of the
// selected block. Buttons: move up, move down, duplicate, delete, plus
// a "Type" dropdown for text-like blocks (paragraph ↔ heading 1/2/3 ↔ quote).

import { useState } from "react";
import type { Block, BlockType } from "../../lib/blocks";
import { t as theme } from "./theme";
import { useT, type T } from "../../lib/i18n";

export interface BlockToolbarProps {
  block: Block;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (dir: -1 | 1) => void;
  onDuplicate: () => void;
  onRemove: () => void;
  replaceSelf: (type: BlockType) => void;
}

type TextTypeChoice =
  | "paragraph"
  | "heading-1"
  | "heading-2"
  | "heading-3"
  | "quote";

function currentTextType(block: Block): TextTypeChoice | null {
  if (block.type === "paragraph") return "paragraph";
  if (block.type === "heading") return (`heading-${block.level}` as TextTypeChoice);
  if (block.type === "quote") return "quote";
  return null;
}

const textOptions = (t: T): { value: TextTypeChoice; label: string }[] => [
  { value: "paragraph", label: t.blockTypeParagraph },
  { value: "heading-1", label: t.blockHeadingLevel(1) },
  { value: "heading-2", label: t.blockHeadingLevel(2) },
  { value: "heading-3", label: t.blockHeadingLevel(3) },
  { value: "quote",     label: t.blockTypeQuote },
];

export function BlockToolbar({
  block,
  canMoveUp,
  canMoveDown,
  onMove,
  onDuplicate,
  onRemove,
  replaceSelf,
}: BlockToolbarProps) {
  const t = useT();
  const TEXT_OPTIONS = textOptions(t);
  const [typeOpen, setTypeOpen] = useState(false);
  const cur = currentTextType(block);

  function applyTypeChoice(c: TextTypeChoice) {
    setTypeOpen(false);
    if (c === cur) return;
    if (c === "paragraph") replaceSelf("paragraph");
    else if (c === "quote") replaceSelf("quote");
    else replaceSelf("heading"); // level is reset to 2 by makeBlock — toolbar can't preserve cross-type
    // Note: switching paragraph→heading drops the inline HTML (heading is plain text by design).
  }

  return (
    <div
      style={{
        position: "absolute",
        top: -36,
        left: 0,
        display: "flex",
        gap: 2,
        padding: 3,
        background: theme.card,
        border: `1px solid ${theme.border}`,
        borderRadius: 6,
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
        zIndex: 10,
        fontFamily: theme.sans,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {cur && (
        <div style={{ position: "relative" }}>
          <ToolbarButton
            onClick={() => setTypeOpen((v) => !v)}
            title={t.blockToolbarChangeType}
          >
            <span style={{ fontSize: 11, padding: "0 4px" }}>
              {TEXT_OPTIONS.find((o) => o.value === cur)?.label || t.blockToolbarChangeType} ▾
            </span>
          </ToolbarButton>
          {typeOpen && (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                marginTop: 4,
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: 6,
                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                minWidth: 140,
                zIndex: 20,
              }}
            >
              {TEXT_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => applyTypeChoice(o.value)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "6px 10px",
                    fontSize: 12,
                    background: cur === o.value ? theme.accentSoft : "transparent",
                    color: cur === o.value ? theme.accent : theme.text,
                    border: "none",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.background =
                      cur === o.value ? theme.accentSoft : theme.hover)
                  }
                  onMouseLeave={(e) =>
                    ((e.currentTarget as HTMLButtonElement).style.background =
                      cur === o.value ? theme.accentSoft : "transparent")
                  }
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <ToolbarButton
        disabled={!canMoveUp}
        onClick={() => onMove(-1)}
        title={t.blockToolbarMoveUp}
      >
        ↑
      </ToolbarButton>
      <ToolbarButton
        disabled={!canMoveDown}
        onClick={() => onMove(1)}
        title={t.blockToolbarMoveDown}
      >
        ↓
      </ToolbarButton>
      <ToolbarButton onClick={onDuplicate} title={t.blockToolbarDuplicate}>
        ⎘
      </ToolbarButton>
      <ToolbarButton onClick={onRemove} title={t.blockToolbarDelete} danger>
        ×
      </ToolbarButton>
    </div>
  );
}

function ToolbarButton({
  onClick,
  disabled,
  title,
  danger,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        minWidth: 26,
        height: 26,
        padding: "0 6px",
        background: "transparent",
        border: "none",
        borderRadius: 4,
        color: danger ? theme.danger : theme.text2,
        fontSize: 14,
        lineHeight: 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? "default" : "pointer",
        transition: theme.transition,
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        (e.currentTarget as HTMLButtonElement).style.background = danger
          ? theme.dangerSoft
          : theme.hover;
      }}
      onMouseLeave={(e) =>
        ((e.currentTarget as HTMLButtonElement).style.background =
          "transparent")
      }
    >
      {children}
    </button>
  );
}
